import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listSuppliers, createSupplier, updateSupplier, deleteSupplier, listGLAccounts } from '../api';
import { formatCurrency } from '../utils/currency';
import { useConfirm } from './ConfirmProvider';

const EMPTY_FORM = {
  name: '', trading_name: '', contact_person: '', email: '', phone: '', address: '',
  tin: '', registration_number: '', bank_name: '', bank_account_number: '', bank_branch_code: '',
  payment_terms_days: 30, is_non_resident: false, default_gl_account: '', notes: '', is_active: true,
};

function SupplierManagement({ authAxios, showFormMessage, userRole }) {
  const confirm = useConfirm();
  const [suppliers, setSuppliers] = useState([]);
  const [glAccounts, setGLAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [formData, setFormData] = useState(EMPTY_FORM);

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchSuppliers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (statusFilter === 'active') params.is_active = true;
      else if (statusFilter === 'inactive') params.is_active = false;
      const data = await listSuppliers(authAxios, params);
      setSuppliers(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load suppliers.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage, statusFilter]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  // Fetch GL accounts for dropdown
  useEffect(() => {
    (async () => {
      try {
        const data = await listGLAccounts(authAxios, { page_size: 500, is_active: true });
        setGLAccounts(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
  }, [authAxios]);

  const openNewForm = () => { setEditingSupplier(null); setFormData(EMPTY_FORM); setShowForm(true); };
  const openEditForm = (s) => {
    setEditingSupplier(s);
    setFormData({
      name: s.name || '', trading_name: s.trading_name || '', contact_person: s.contact_person || '',
      email: s.email || '', phone: s.phone || '', address: s.address || '',
      tin: s.tin || '', registration_number: s.registration_number || '',
      bank_name: s.bank_name || '', bank_account_number: s.bank_account_number || '',
      bank_branch_code: s.bank_branch_code || '', payment_terms_days: s.payment_terms_days || 30,
      is_non_resident: s.is_non_resident || false, default_gl_account: s.default_gl_account || '',
      notes: s.notes || '', is_active: s.is_active,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, default_gl_account: formData.default_gl_account || null };
    try {
      if (editingSupplier) {
        await updateSupplier(authAxios, editingSupplier.id, payload);
        showFormMessage('success', 'Supplier updated.');
      } else {
        await createSupplier(authAxios, payload);
        showFormMessage('success', 'Supplier created.');
      }
      setShowForm(false);
      fetchSuppliers();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to save supplier.');
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete Supplier',
      message: 'Delete this supplier? This cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteSupplier(authAxios, id);
      showFormMessage('success', 'Supplier deleted.');
      fetchSuppliers();
    } catch { showFormMessage('error', 'Cannot delete supplier with existing bills.'); }
  };

  const filtered = useMemo(() => {
    if (!searchTerm) return suppliers;
    const term = searchTerm.toLowerCase();
    return suppliers.filter(s => s.name.toLowerCase().includes(term) || (s.email || '').toLowerCase().includes(term) || (s.contact_person || '').toLowerCase().includes(term));
  }, [suppliers, searchTerm]);

  const updateField = (field, value) => setFormData(f => ({ ...f, [field]: value }));

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Supplier Management</h2>
          <p className="text-gray-500 text-sm mt-1">Manage vendors and service providers</p>
        </div>
        {canManage && (
          <button onClick={openNewForm} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">+ New Supplier</button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input type="text" placeholder="Search suppliers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{editingSupplier ? 'Edit Supplier' : 'New Supplier'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Name *</label>
                  <input type="text" required value={formData.name} onChange={e => updateField('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                  <input type="text" value={formData.contact_person} onChange={e => updateField('contact_person', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="text" value={formData.phone} onChange={e => updateField('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea value={formData.address} onChange={e => updateField('address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">TIN (Tax ID)</label>
                  <input type="text" value={formData.tin} onChange={e => updateField('tin', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                  <input type="text" value={formData.registration_number} onChange={e => updateField('registration_number', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trading Name</label>
                  <input type="text" value={formData.trading_name} onChange={e => updateField('trading_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms (days)</label>
                  <input type="number" value={formData.payment_terms_days} onChange={e => updateField('payment_terms_days', parseInt(e.target.value) || 30)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default GL Account</label>
                <select value={formData.default_gl_account || ''} onChange={e => updateField('default_gl_account', e.target.value ? parseInt(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">— None —</option>
                  {glAccounts.filter(a => a.category === 'EXPENSE' || a.category === 'COST_OF_SALES').map(a => (
                    <option key={a.id} value={a.id}>{a.code} – {a.name}</option>
                  ))}
                </select>
              </div>

              <h4 className="text-sm font-semibold text-gray-600 mt-2">Banking Details</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                  <input type="text" value={formData.bank_name} onChange={e => updateField('bank_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                  <input type="text" value={formData.bank_account_number} onChange={e => updateField('bank_account_number', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch Code</label>
                  <input type="text" value={formData.bank_branch_code} onChange={e => updateField('bank_branch_code', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={formData.is_non_resident} onChange={e => updateField('is_non_resident', e.target.checked)} />
                  Non-Resident Supplier (for WHT purposes)
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={formData.notes} onChange={e => updateField('notes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={formData.is_active} onChange={e => updateField('is_active', e.target.checked)} />
                Active
              </label>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
                  {editingSupplier ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Supplier List */}
      {isLoading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm"><p className="text-gray-500">No suppliers found.</p></div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Terms</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Non-Resident</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                {canManage && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{s.name}</td>
                  <td className="px-4 py-3 text-gray-600">{s.contact_person || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{s.email || '—'}</td>
                  <td className="px-4 py-3 text-center text-gray-600">{s.payment_terms_days} days</td>
                  <td className="px-4 py-3 text-center">
                    {s.is_non_resident ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Yes</span>
                    ) : <span className="text-gray-400">No</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-800">
                    {s.outstanding_balance != null ? formatCurrency(s.outstanding_balance) : '—'}
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEditForm(s)} className="text-blue-600 hover:text-blue-800 text-xs font-medium mr-3">Edit</button>
                      <button onClick={() => handleDelete(s.id)} className="text-red-500 hover:text-red-700 text-xs font-medium">Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SupplierManagement;
